import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { DEFAULT_PAGE_SIZE } from "@cpg/common";

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DataTable, type DataTableColumn } from "@/components/data-table";
import { Amount } from "@/components/amount";
import { cn } from "@/lib/utils";
import { getLedgerTransactions } from "@/api/ledgers";
import { type LedgerTransactionItem } from "@/api/types";

// Task 28: the one transaction-history drill-down dialog, shared between the
// global Ledger list (Part B) and the merchant detail page's Ledger tab
// (Part D, retrofit) — per the task brief's explicit "reuse the one dialog
// component, don't build a second copy" instruction. Colocated directly
// under routes/ (not components/) since it's route-specific UI wired to two
// specific pages, not shared-kit UI (components/ is the Task 19-23 shared-kit
// directory, out of scope here).
//
// Controlled from outside: the caller owns a `LedgerDrillDownTarget | null`
// state slot and passes it down, same controlled-Dialog convention every
// other dialog in this app uses (see _app.merchants.tsx's CreateMerchantDialog).
// Pagination is local `useState` (not `useTableState` — a dialog isn't a
// route, per the brief), reset to page 1 whenever a different ledger is
// opened.

export interface LedgerDrillDownTarget {
  /** Which side is shown first when the dialog opens. */
  type: "merchant" | "project";
  ledgerId: string;
  /**
   * The sibling ledger's id. Both current callers (the global Ledger list
   * and the merchant detail page's Ledger tab) always know both sides and
   * pass it, but this stays optional rather than required — the dialog
   * degrades to a plain single-side view without it, for any future caller
   * that only has one ledger id. When present, a Merchant/Project toggle
   * appears so both sides are reachable from the one button.
   */
  otherLedgerId?: string;
  /** `LedgerTransactionItem` carries no symbol of its own — the opening row already knows its asset. */
  assetSymbol: string;
  /** Shown under the dialog title for context, e.g. "Acme Inc · USDT · ETHEREUM". */
  subtitle: string;
}

export interface LedgerTransactionsDialogProps {
  target: LedgerDrillDownTarget | null;
  onOpenChange: (open: boolean) => void;
}

function formatTimestamp(value: string): string {
  return new Date(value).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

// Same truncation shape as hash-link.tsx's `truncateHash` — this id is an
// internal UUID, not a hash, so it gets its own small helper rather than
// reusing that component (no explorer link, no copy button; just a shorter
// display with the full value on hover).
function truncateId(value: string): string {
  return value.length <= 12 ? value : `${value.slice(0, 6)}…${value.slice(-4)}`;
}

/** Same pill-switcher shape as `period-switcher.tsx` — only rendered when `target.otherLedgerId` makes both sides reachable. */
function LedgerSideSwitcher({
  active,
  onChange,
}: {
  active: "merchant" | "project";
  onChange: (side: "merchant" | "project") => void;
}) {
  const options: Array<{ value: "merchant" | "project"; label: string }> = [
    { value: "merchant", label: "Merchant ledger" },
    { value: "project", label: "Project ledger" },
  ];

  return (
    <div className="inline-flex items-center rounded-full border border-border p-0.5">
      {options.map((option) => {
        const isActive = option.value === active;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            aria-pressed={isActive}
            className={cn(
              "rounded-full px-3 py-1 text-xs font-medium transition-colors cursor-pointer",
              isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted",
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export function LedgerTransactionsDialog({ target, onOpenChange }: LedgerTransactionsDialogProps) {
  const [side, setSide] = useState<"merchant" | "project">("merchant");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // A newly-opened target always starts on its own `type` — a stale side or
  // page number from a previously-opened ledger would be meaningless
  // against it.
  useEffect(() => {
    setSide(target?.type ?? "merchant");
    setPage(1);
  }, [target?.type, target?.ledgerId, target?.otherLedgerId]);

  const activeLedgerId = side === target?.type ? target?.ledgerId : target?.otherLedgerId;

  const { data, isPending, isError } = useQuery({
    queryKey: ["ledger-transactions", side, activeLedgerId, page, pageSize],
    queryFn: () => getLedgerTransactions(side, activeLedgerId!, { page, pageSize }),
    enabled: activeLedgerId !== undefined,
  });

  const columns: DataTableColumn<LedgerTransactionItem>[] = [
    {
      key: "operation",
      header: "Operation",
      cell: (t) => t.operation,
    },
    {
      key: "amount",
      header: "Amount",
      className: "text-right",
      cell: (t) => <Amount native={t.amountNative} symbol={target?.assetSymbol ?? ""} usd={t.amountUsd} />,
    },
    // The merchant ledger has no gas-cost concept at all (always null there,
    // per the backend's own doc comment) — omitted outright on that side
    // rather than shown as a column of dashes.
    ...(side === "project"
      ? [
          {
            key: "gasCost",
            header: "Gas cost",
            className: "text-right",
            cell: (t: LedgerTransactionItem) => <Amount native={t.gasCostNative} symbol={target?.assetSymbol ?? ""} usd={t.gasCostUsd} />,
          },
        ]
      : []),
    {
      key: "invoicePublicId",
      header: "Invoice",
      cell: (t) => t.invoicePublicId ?? "—",
    },
    {
      key: "withdrawalId",
      header: "Withdrawal",
      cell: (t) =>
        t.withdrawalId ? (
          <span className="font-mono text-xs" title={t.withdrawalId}>
            {truncateId(t.withdrawalId)}
          </span>
        ) : (
          "—"
        ),
    },
    {
      key: "createdAt",
      header: "Created",
      cell: (t) => formatTimestamp(t.createdAt),
    },
  ];

  return (
    <Dialog open={target !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          {/* Static — "Merchant"/"Project" no longer alternates in the title itself,
              since the pill switcher below already shows which ledger side is active. */}
          <DialogTitle>Account History</DialogTitle>
          {target && <DialogDescription>{target.subtitle}</DialogDescription>}
        </DialogHeader>

        {target?.otherLedgerId !== undefined && (
          <LedgerSideSwitcher
            active={side}
            onChange={(next) => {
              setSide(next);
              setPage(1);
            }}
          />
        )}

        <DataTable
          columns={columns}
          items={data?.data ?? []}
          total={data?.total ?? 0}
          page={page}
          pageSize={pageSize}
          loading={isPending}
          error={isError ? "Failed to load transactions." : null}
          onPageChange={setPage}
          onPageSizeChange={(nextPageSize) => {
            setPageSize(nextPageSize);
            setPage(1);
          }}
          getRowKey={(t) => t.id}
          emptyMessage="No transactions yet."
        />
      </DialogContent>
    </Dialog>
  );
}
